import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Zap, PiggyBank, CalendarClock, Leaf, Sparkles } from "lucide-react";

const TARIFF = 1450; // Rp per kWh (asumsi industri/komersial)
const OFFSET = 0.7; // 70% konsumsi tercover PLTS
const SUN_HOURS = 4; // jam surya efektif per hari (Indonesia)
const COST_PER_KWP = 12_000_000; // Rp per kWp terpasang
const CO2_PER_KWH = 0.85; // kg CO2 per kWh grid

function rupiah(n) {
  if (n >= 1_000_000_000)
    return `Rp ${(n / 1_000_000_000).toFixed(1).replace(".", ",")} M`;
  if (n >= 1_000_000)
    return `Rp ${(n / 1_000_000).toFixed(1).replace(".", ",")} Jt`;
  return `Rp ${Math.round(n).toLocaleString("id-ID")}`;
}

export default function SavingsCalculator() {
  const [bill, setBill] = useState(50); // dalam juta rupiah / bulan

  const result = useMemo(() => {
    const monthlyBill = bill * 1_000_000;
    const monthlyKwh = monthlyBill / TARIFF;
    const dailyKwh = monthlyKwh / 30;
    const kwpVal = (dailyKwh * OFFSET) / SUN_HOURS;
    const annualSavings = monthlyBill * 12 * OFFSET;
    const systemCost = kwpVal * COST_PER_KWP;
    const payback = annualSavings > 0 ? systemCost / annualSavings : 0;
    const annualKwhOffset = monthlyKwh * 12 * OFFSET;
    const co2Tons = (annualKwhOffset * CO2_PER_KWH) / 1000;
    
    return {
      // Tampilkan desimal jika kapasitas di bawah 10 kWp
      kwp: kwpVal < 10 ? kwpVal.toFixed(1).replace(".", ",") : Math.round(kwpVal).toLocaleString("id-ID"),
      annualSavings,
      payback: payback.toFixed(1).replace(".", ","),
      co2Tons: co2Tons < 1 ? co2Tons.toFixed(1).replace(".", ",") : Math.round(co2Tons).toLocaleString("id-ID"),
    };
  }, [bill]);

  const results = [
    {
      icon: Zap,
      label: "Estimasi Kapasitas",
      value: `${result.kwp.toLocaleString("id-ID")} kWp`,
      color: "text-blue-900",
      bg: "bg-blue-50",
      testid: "calculator-result-capacity",
    },
    {
      icon: PiggyBank,
      label: "Annual Savings",
      value: rupiah(result.annualSavings),
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      testid: "calculator-result-savings",
    },
    {
      icon: CalendarClock,
      label: "Payback Period",
      value: `${result.payback} thn`,
      color: "text-amber-600",
      bg: "bg-amber-50",
      testid: "calculator-result-payback",
    },
    {
      icon: Leaf,
      label: "Reduksi CO₂ / Tahun",
      value: `${result.co2Tons.toLocaleString("id-ID")} ton`,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      testid: "calculator-result-co2",
    },
  ];

  return (
    <div
      data-testid="savings-calculator"
      className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl shadow-blue-900/5"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-900 text-white">
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900">
            Estimate Your Solar Savings
          </h3>
          <p className="text-sm text-slate-500">
            Slide to match your monthly electricity bill
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-end justify-between">
          <label className="text-sm font-semibold text-slate-600">
            Monthly Electricity Bill
          </label>
          <span
            data-testid="calculator-bill-value"
            className="font-display text-2xl font-extrabold text-blue-900"
          >
            Rp {bill.toLocaleString("id-ID")} Jt
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="500"
          step="5"
          value={bill}
          data-testid="calculator-bill-input"
          onChange={(e) => setBill(Number(e.target.value))}
          className="mt-4 w-full cursor-pointer accent-emerald-600"
          style={{ accentColor: "#059669" }}
        />
        <div className="mt-1 flex justify-between text-xs text-slate-400">
          <span>Rp 1 Jt</span>
          <span>Rp 500 Jt</span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4">
        {results.map((r) => (
          <motion.div
            key={r.label}
            data-testid={r.testid}
            className={`rounded-2xl ${r.bg} p-4`}
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <r.icon className={`h-5 w-5 ${r.color}`} />
            <div className={`mt-3 font-display text-xl font-extrabold ${r.color}`}>
              {r.value}
            </div>
            <div className="text-xs font-medium text-slate-500">{r.label}</div>
          </motion.div>
        ))}
      </div>

      <p className="mt-5 text-xs leading-relaxed text-slate-400">
      </p>
    </div>
  );
}
